/* eslint-disable react/jsx-no-bind */
import {
  AgentCall,
  Conversation,
  StripeAssistantLiveAnswersResponse,
  InlineDataConversationMessage,
} from '@stripe-internal/conversations-client';
import {InlineDataProps} from '@stripe-internal/inline-data';
import {LiveAnswersResponse} from './LiveAnswersResponse';
import {FullyAsyncResponse} from './FullyAsyncResponse';

type Props = {
  conversation: Conversation;
  message: InlineDataConversationMessage;
  agentCallRenderer?: (agentCall: AgentCall) => JSX.Element | null;
};

export const LiveAnswersResponseContainer = ({
  conversation,
  message,
  agentCallRenderer,
}: Props) => {
  function onError() {
    if (!conversation.isTabMostRecent()) {
      return;
    }

    conversation.action({
      name: 'inline-data-result',
      message,
      result: {type: 'error'},
    });
  }

  function onComplete(
    content: string,
    answerType: 'unanswerable' | 'answerable' = 'answerable',
  ) {
    if (!conversation.isTabMostRecent()) {
      return;
    }

    // Update the message. If an error happened, the message will the saved with
    // `isDone` as true but without any content. This is consistent with how it
    // currently renders: it will render nothing and the backend has sent a new
    // message to the user about the error which is rendered separately
    const updatedData = {
      ...(message.inlineData.data as StripeAssistantLiveAnswersResponse),
      is_streamed: true,
      content,
    };
    const updatedMessage = {
      ...message,
      inlineData: {...message.inlineData, data: updatedData},
    };

    conversation.action({
      name: 'inline-data-result',
      message: updatedMessage,
      result: {type: 'success', data: {outcome: answerType}},
    });
  }

  const inlineData = message.inlineData as InlineDataProps;
  const data = inlineData.data as unknown as StripeAssistantLiveAnswersResponse;

  let cachedContent = null;
  if (data.is_streamed) {
    cachedContent = data.content;
  } else if (inlineData.isDone) {
    // This is an error state. For the inline data to be marked done but the
    // data is not completed streaming, that means something went wrong. In
    // the future we can make this clearer
    cachedContent = '';
  }

  const isFullyAsync =
    'type' in data && (data as {type: string}).type === 'fully_async';

  if (isFullyAsync) {
    return (
      <FullyAsyncResponse
        conversation={conversation}
        messageId={message.id}
        saResponseId={data.sa_response_id}
        llmConversationId={data.llm_conversation_id}
        cacheUuid={data.cache_uuid}
        sources={data.sources}
        cachedContent={cachedContent}
        agentCallRenderer={agentCallRenderer}
        onComplete={onComplete}
        onError={onError}
      />
    );
  }

  return (
    <LiveAnswersResponse
      conversation={conversation}
      messageId={message.id}
      saResponseId={data.sa_response_id}
      llmConversationId={data.llm_conversation_id}
      cacheUuid={data.cache_uuid}
      sources={data.sources}
      cachedContent={cachedContent}
      agentCallRenderer={agentCallRenderer}
      onComplete={onComplete}
      onError={onError}
    />
  );
};
